import {
  WeightingReport,
  WeightingReportQuestion,
} from "types/engineOutput.types"

/** One row of a weighting question's table. */
export interface WeightingRow {
  key: string
  group: string
  respondents: number
  /** The target used, e.g. "51.7" or "33.33 (typed 33.3)"; empty without a target. */
  target: string
  achieved: string
  /** Achieved minus target, e.g. "+0.00"; empty without a target. */
  difference: string
  /** True when achieved is 0.01 points or more away from the target. */
  off: boolean
}

/** A percentage with at most 2 decimals and no trailing zeros: 51.7, 33.33, 20. */
export const formatTarget = (value: number): string =>
  String(Math.round(value * 100) / 100)

const formatDifference = (value: number): string => {
  const rounded = Math.round(value * 100) / 100
  const fixed = Math.abs(rounded).toFixed(2)
  return rounded < 0 ? `-${fixed}` : `+${fixed}`
}

/** The rows of one question: each group, then "No group" when anyone is in none. */
export const weightingRows = (
  question: WeightingReportQuestion
): WeightingRow[] => {
  const rows: WeightingRow[] = question.groups.map((g) => {
    if (g.target === null) {
      return {
        key: g.label,
        group: g.label,
        respondents: g.respondents,
        target: "",
        achieved: g.achieved.toFixed(2),
        difference: "",
        off: false,
      }
    }
    const diff = g.achieved - g.target
    return {
      key: g.label,
      group: g.label,
      respondents: g.respondents,
      target:
        g.typed === undefined
          ? formatTarget(g.target)
          : `${formatTarget(g.target)} (typed ${formatTarget(g.typed)})`,
      achieved: g.achieved.toFixed(2),
      difference: formatDifference(diff),
      off: Math.abs(diff) >= 0.005,
    }
  })
  if (question.no_group && question.no_group.respondents > 0) {
    rows.push({
      key: "__no_group__",
      group: "No group",
      respondents: question.no_group.respondents,
      target: "",
      achieved: question.no_group.achieved.toFixed(2),
      difference: "",
      off: false,
    })
  }
  return rows
}

/** "400 respondents, weights 0.61 to 1.92, efficiency 91.4%, effective base 366". */
export const weightingSummary = (report: WeightingReport): string =>
  [
    `${report.respondents} respondents`,
    `weights ${report.min.toFixed(2)} to ${report.max.toFixed(2)}`,
    `efficiency ${report.efficiency.toFixed(1)}%`,
    `effective base ${Math.round(report.effective_base)}`,
  ].join(", ")

/** "Weighting: weights" or "Weighting: weights, IF Wave//W1". */
export const weightingTitle = (report: WeightingReport): string =>
  `Weighting: ${report.weights}${report.filter ? `, IF ${report.filter}` : ""}`

/** The last report for a weight field, matched on the field as typed in the step. */
export const reportForWeights = (
  reports: WeightingReport[],
  weightsField: string
): WeightingReport | undefined => {
  const name = weightsField.trim()
  return [...reports].reverse().find((r) => r.weights === name)
}
